import { ActionIcon, Button, FileButton, Group, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconDownload, IconPaperclip, IconTrash } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { AttachmentDto } from '@ledokol/contracts'
import { deleteAttachment, downloadAttachment, uploadAttachment } from '../../shared/api/attachments'
import { isApiError } from '../../shared/api/client'
import { useAuthStore } from '../auth/store'

const MAX_BYTES = 25 * 1024 * 1024

export function TaskAttachments({ taskId, attachments }: { taskId: string; attachments: AttachmentDto[] }) {
  const qc = useQueryClient()
  const me = useAuthStore((s) => s.user)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['task', taskId] })
    qc.invalidateQueries({ queryKey: ['task', taskId, 'activity'] })
  }

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadAttachment(taskId, file),
    onSuccess: invalidate,
    onError: (e: unknown) => notifications.show({ message: uploadErrorText(e), color: 'red' }),
  })

  const deleteMut = useMutation({
    mutationFn: (attachmentId: string) => deleteAttachment(taskId, attachmentId),
    onSuccess: invalidate,
    onError: () => notifications.show({ message: 'Не удалось удалить файл', color: 'red' }),
  })

  function onPick(file: File | null) {
    if (!file) return
    if (file.size > MAX_BYTES) {
      notifications.show({ message: 'Файл больше 25 МБ', color: 'red' })
      return
    }
    uploadMut.mutate(file)
  }

  // observer — read-only: сервер вернёт 403, кнопку не показываем вовсе
  const canUpload = me?.role !== 'observer'

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          Вложения{attachments.length > 0 ? ` · ${attachments.length}` : ''}
        </Text>
        {canUpload && (
          <FileButton onChange={onPick}>
            {(props) => (
              <Button
                {...props}
                size="compact-xs"
                variant="light"
                leftSection={<IconPaperclip size={13} />}
                loading={uploadMut.isPending}
              >
                Прикрепить файл
              </Button>
            )}
          </FileButton>
        )}
      </Group>

      {attachments.map((a) => (
        <Group key={a.id} gap={6} wrap="nowrap">
          <IconPaperclip size={14} color="var(--mantine-color-dimmed)" />
          <Text size="sm" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {a.file_name}
          </Text>
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {humanSize(a.size_bytes)} · {a.added_by.full_name} · {dayjs(a.created_at).format('D MMM HH:mm')}
          </Text>
          <ActionIcon
            variant="subtle"
            color="blue"
            title="Скачать"
            onClick={() =>
              downloadAttachment(taskId, a).catch(() =>
                notifications.show({ message: 'Не удалось скачать файл', color: 'red' }),
              )
            }
          >
            <IconDownload size={15} />
          </ActionIcon>
          {(me?.id === a.added_by.id || me?.role === 'admin') && (
            <ActionIcon
              variant="subtle"
              color="red"
              title="Удалить"
              loading={deleteMut.isPending && deleteMut.variables === a.id}
              onClick={() => deleteMut.mutate(a.id)}
            >
              <IconTrash size={15} />
            </ActionIcon>
          )}
        </Group>
      ))}
    </Stack>
  )
}

function uploadErrorText(e: unknown): string {
  if (isApiError(e)) {
    const code = e.response?.data?.error?.code
    if (code === 'invalid_file_type') return 'Такой тип файла запрещён'
    if (e.response?.status === 413) return 'Файл больше 25 МБ'
  }
  return 'Не удалось загрузить файл'
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}
